/**
 * Combined-listing availability rules for variant-picker-v2.
 * Keep in sync with test-harness/pdp/piv2-availability-rules.mjs
 */
(function (global) {
  'use strict';

  function normalizeOptionValue(value) {
    return String(value ?? '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function getChildVariantStatus(childVariants, optionNames) {
    if (!Array.isArray(childVariants) || !childVariants.length) return null;

    const matchingExisting = childVariants.filter((variant) => {
      const variantOptions =
        Array.isArray(variant?.options) && variant.options.length
          ? variant.options
          : [variant?.option1, variant?.option2, variant?.option3].filter((v) => v !== undefined);

      if (!variantOptions.length) return false;

      return optionNames.every((expected, index) => {
        if (expected == null || expected === '') return true;
        return normalizeOptionValue(variantOptions[index] || '') === normalizeOptionValue(expected);
      });
    });

    const matchingAvailable = matchingExisting.filter((variant) =>
      Boolean(variant?.available ?? variant?.availableForSale ?? variant?.sellable)
    );

    return {
      exists: matchingExisting.length > 0,
      isAvailable: matchingAvailable.length > 0,
      matchingExistingCount: matchingExisting.length,
      matchingAvailableCount: matchingAvailable.length,
    };
  }

  function resolveCombinedListingValueState({
    backendState,
    childStatus,
    optionIndex,
    colorOptionIndex = 0,
    combinedListingChild = false,
    variantsTruncated = false,
    isSiblingValue = false,
  }) {
    if (!combinedListingChild || optionIndex === colorOptionIndex || !childStatus) {
      return backendState;
    }

    // A sibling value's variants live on another child product (listings can
    // split children on non-color axes too, e.g. Color × Pocket Liner), so the
    // current child's embedded variants can never attest to it — the
    // parent-scoped state is authoritative.
    if (isSiblingValue) return backendState;

    if (variantsTruncated) return backendState;

    if (!childStatus.exists) return 'notexist';
    if (childStatus.isAvailable || backendState === 'available') return 'available';
    return 'oos';
  }

  function resolveCombinedListingCtaState({
    backendCtaState,
    selectionComplete = false,
    resolvedVariant = null,
    combinedListingChild = false,
    variantsTruncated = false,
  }) {
    if (!combinedListingChild || !selectionComplete) return backendCtaState;
    if (!resolvedVariant && !variantsTruncated) {
      return 'unavailable';
    }
    return backendCtaState;
  }

  function unlistedParentValueKey(optionName, valueName) {
    return `${normalizeOptionValue(optionName)}:${normalizeOptionValue(valueName)}`;
  }

  function collectUnlistedFromOptionAxes(axes, keys) {
    if (!Array.isArray(axes)) return;
    for (const axis of axes) {
      const optionName = axis?.name || '';
      for (const value of axis?.optionValues || []) {
        if (value?.fromUnlistedParentVariants !== true) continue;
        if (!value?.name) continue;
        keys.add(unlistedParentValueKey(optionName, value.name));
      }
    }
  }

  function buildUnlistedParentValueKeys(bundle) {
    const keys = new Set();
    if (!bundle || typeof bundle !== 'object') return keys;

    collectUnlistedFromOptionAxes(bundle.options, keys);
    collectUnlistedFromOptionAxes(bundle.childOptions, keys);

    if (keys.size > 0) return keys;

    const resolutionValues = bundle.meta?.parentUnlistedResolution?.values;
    if (!Array.isArray(resolutionValues)) return keys;

    for (const entry of resolutionValues) {
      const optionName = entry?.option || '';
      for (const valueName of entry?.values || []) {
        if (!valueName) continue;
        keys.add(unlistedParentValueKey(optionName, valueName));
      }
    }

    return keys;
  }

  function isUnlistedParentValue(keys, optionName, valueName) {
    if (!(keys instanceof Set) || keys.size === 0) return false;
    return keys.has(unlistedParentValueKey(optionName, valueName));
  }

  function collectChildAxisValues(childVariants, optionIndex) {
    const values = new Set();
    if (!Array.isArray(childVariants) || optionIndex == null) return values;

    for (const variant of childVariants) {
      const variantOptions =
        Array.isArray(variant?.options) && variant.options.length
          ? variant.options
          : [variant?.option1, variant?.option2, variant?.option3].filter((v) => v !== undefined);
      const value = variantOptions[optionIndex];
      if (value != null && value !== '') values.add(String(value));
    }

    return values;
  }

  /**
   * Classify which of a combined-listing parent's option axes are
   * product-split ("sibling") axes vs. axes owned by each child product.
   *
   * Primary rule (live-verified on the Storefront API): a variant's title is
   * the CHILD's own option values joined by " / ", so its segment count x is
   * the number of child-owned options; with N parent axes, the FIRST N-x axes
   * are the product-split axes (e.g. Color, Pocket Liner) and the LAST x are
   * child axes (waist, fit, length).
   *
   * Fallback rule: when childOptionNames is provided (the child product's own
   * options), sibling axes are the parent axes whose name is absent from the
   * child's set — order-independent, used when a title is ambiguous (an
   * option value containing " / " would skew the segment count).
   */
  function classifySiblingAxes({ parentOptionNames, firstVariantTitle = '', childOptionNames = null }) {
    const names = Array.isArray(parentOptionNames) ? parentOptionNames : [];
    const n = names.length;
    if (!n) return { siblingAxisIndices: [], method: null };

    if (Array.isArray(childOptionNames) && childOptionNames.length) {
      const childSet = new Set(childOptionNames.map((name) => normalizeOptionValue(name)));
      const siblingAxisIndices = [];
      names.forEach((name, index) => {
        if (!childSet.has(normalizeOptionValue(name))) siblingAxisIndices.push(index);
      });
      return { siblingAxisIndices, method: 'options-diff' };
    }

    const title = String(firstVariantTitle || '').trim();
    if (!title) return { siblingAxisIndices: [], method: null };

    const x = title.split(' / ').length;
    if (x <= 0 || x > n) return { siblingAxisIndices: [], method: null };

    const siblingAxisIndices = [];
    for (let index = 0; index < n - x; index++) siblingAxisIndices.push(index);
    return { siblingAxisIndices, method: 'title' };
  }

  /**
   * Decode Shopify's encoded variant combo strings (Storefront API
   * Product.encodedVariantExistence / encodedVariantAvailability, "v1_" prefix)
   * into a Set of 'i:j:k' option-value index keys. Same wire format the BFF
   * served; keep the algorithm in sync with the picker's internal BFF decoder
   * (assets/variant-picker-v2.js decodeEncodedVariantAvailability).
   */
  function decodeEncodedVariantCombos(encoded) {
    const match = /^(v\d+)_/.exec(encoded || '');
    const version = match ? match[1] : null;
    const payload = match ? encoded.slice(match[0].length) : encoded || '';
    if (version && version !== 'v1') {
      throw new Error(`Unsupported encoded variant combo version prefix: ${version}`);
    }

    const expandValueToken = (token) => {
      const out = [];
      for (const part of token.trim().split(/\s+/)) {
        if (!part) continue;
        const rangeMatch = part.match(/^(\d+)-(\d+)$/);
        if (rangeMatch) {
          const from = Number(rangeMatch[1]);
          const to = Number(rangeMatch[2]);
          if (to < from) throw new Error(`Invalid range in token "${token}"`);
          for (let value = from; value <= to; value++) out.push(value);
        } else if (/^\d+$/.test(part)) {
          out.push(Number(part));
        } else {
          throw new Error(`Invalid value token part "${part}" in "${token}"`);
        }
      }
      return out;
    };

    const combos = new Set();
    const prefix = [];
    let i = 0;
    let currentToken = '';

    const emitLeaf = () => {
      if (!currentToken) return;
      for (const value of expandValueToken(currentToken)) {
        combos.add([...prefix, value].join(':'));
      }
      currentToken = '';
    };

    while (i < payload.length) {
      const ch = payload[i];

      if ((ch >= '0' && ch <= '9') || ch === ' ' || ch === '-') {
        currentToken += ch;
        i++;
        continue;
      }

      if (ch === ':') {
        if (!currentToken) throw new Error(`Unexpected ':' at position ${i}`);
        prefix.push(Number(currentToken));
        currentToken = '';
        i++;
        continue;
      }

      if (ch === ',') {
        emitLeaf();
        let commaCount = 0;
        while (i < payload.length && payload[i] === ',') {
          commaCount++;
          i++;
        }
        for (let pop = 0; pop < commaCount && prefix.length > 0; pop++) {
          prefix.pop();
        }
        continue;
      }

      throw new Error(`Unexpected character "${ch}" at position ${i}`);
    }

    if (currentToken) emitLeaf();

    return combos;
  }

  /**
   * Complete a partial selection (null slots) into a FULL option-value index
   * array using the exist combos — needed because the Storefront API's
   * variantBySelectedOptions only resolves FULL selections. Non-null slots
   * are hard constraints; `preferred` (e.g. the pre-commit selection) breaks
   * ties so the completion keeps the shopper's current identity values on
   * axes the reconcile cleared. Returns null when no exist combo satisfies
   * the constraints; an already-complete selection passes through.
   */
  function completeSelectionFromExistCombos({ selection, preferred = null, existArrays }) {
    if (!Array.isArray(selection)) return null;
    if (selection.length && selection.every((value) => value != null)) return [...selection];
    if (!Array.isArray(existArrays) || !existArrays.length) return null;

    let best = null;
    let bestScore = -1;
    for (const combo of existArrays) {
      let matches = true;
      for (let i = 0; i < selection.length; i++) {
        if (selection[i] != null && combo[i] !== selection[i]) {
          matches = false;
          break;
        }
      }
      if (!matches) continue;

      if (!Array.isArray(preferred)) return [...combo]; // first (sorted) match wins

      let score = 0;
      for (let i = 0; i < selection.length; i++) {
        if (selection[i] == null && preferred[i] != null && combo[i] === preferred[i]) score++;
      }
      if (score > bestScore) {
        best = combo;
        bestScore = score;
      }
    }
    return best ? [...best] : null;
  }

  global.Piv2AvailabilityRules = {
    normalizeOptionValue,
    getChildVariantStatus,
    resolveCombinedListingValueState,
    resolveCombinedListingCtaState,
    buildUnlistedParentValueKeys,
    isUnlistedParentValue,
    collectChildAxisValues,
    classifySiblingAxes,
    decodeEncodedVariantCombos,
    completeSelectionFromExistCombos,
  };
})(typeof globalThis !== 'undefined' ? globalThis : window);
